import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { LogOut, Menu } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import ConfirmDialog from "@/components/common/ConfirmDialog";

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [confirmLogout, setConfirmLogout] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <header className="sticky top-0 z-40 border-b border-edge bg-linen/80 backdrop-blur-xl">
      <div className="mx-auto flex h-16 max-w-[1400px] items-center justify-between gap-3 px-4 sm:px-6 lg:px-12">
        <Link to="/" className="flex min-w-0 items-center gap-2" data-testid="header-logo">
          <img src="/logo.svg" alt="Stoop" className="h-8 w-auto" />
          <span className="overline ml-2 hidden lg:block">run your deals in concert</span>
        </Link>
        <nav className="hidden items-center gap-4 md:flex lg:gap-6" aria-label="Primary navigation">
          {user ? (
            <>
              <Link to="/overview" className="text-sm hover:text-moss transition-colors" data-testid="nav-dashboard">Dashboard</Link>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setConfirmLogout(true)}
                className="hover:text-rust"
                data-testid="nav-logout"
              >
                <LogOut data-icon="inline-start" /> Sign out
              </Button>
            </>
          ) : (
            <>
              <Link to="/login" className="text-sm hover:text-moss transition-colors" data-testid="nav-login">Sign in</Link>
              <Button variant="dashboardPrimary" asChild data-testid="nav-register">
                <Link to="/register">Begin</Link>
              </Button>
            </>
          )}
        </nav>

        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetTrigger asChild>
            <Button type="button" variant="ghost" size="icon" className="shrink-0 md:hidden" aria-label="Open navigation menu" data-testid="mobile-nav-trigger">
              <Menu />
            </Button>
          </SheetTrigger>
          <SheetContent side="right" className="flex w-[min(88vw,360px)] flex-col gap-6 bg-linen p-5">
            <SheetHeader className="pr-8 text-left">
              <SheetTitle>
                <img src="/logo.svg" alt="Stoop" className="h-8 w-auto" />
              </SheetTitle>
              <SheetDescription>Navigate your workspace and account.</SheetDescription>
            </SheetHeader>
            <Separator className="bg-edge" />

            {user ? (
              <div className="flex min-h-0 flex-1 flex-col gap-2">
                <Link to="/overview" onClick={() => setMobileOpen(false)} className="rounded-xl px-4 py-3 text-sm font-medium text-ink hover:bg-bone" data-testid="mobile-nav-dashboard">Dashboard</Link>
                <div className="mt-auto flex flex-col gap-3 pt-4">
                  <Separator className="bg-edge" />
                  <p className="truncate px-1 text-xs text-stone">{user.name}</p>
                  <Button type="button" variant="dashboardOutline" className="w-full" onClick={() => { setMobileOpen(false); setConfirmLogout(true); }} data-testid="mobile-nav-logout">
                    <LogOut data-icon="inline-start" /> Sign out
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                <Button variant="dashboardOutline" className="w-full" asChild><Link to="/login" onClick={() => setMobileOpen(false)}>Sign in</Link></Button>
                <Button variant="dashboardPrimary" className="w-full" asChild><Link to="/register" onClick={() => setMobileOpen(false)}>Begin</Link></Button>
              </div>
            )}
          </SheetContent>
        </Sheet>
      </div>

      {confirmLogout && (
        <ConfirmDialog
          open
          onOpenChange={setConfirmLogout}
          title="Sign out?"
          description="You'll need to sign in again to get back into your workspace."
          confirmLabel="Sign out"
          tone="default"
          onConfirm={() => { logout(); navigate("/"); }}
          testId="logout-confirm-dialog"
        />
      )}
    </header>
  );
}
